import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, CheckCheck, Gavel, Trophy, MessageSquare, Star, CheckCircle2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const ICONS = {
  bid: Gavel, accepted: Trophy, message: MessageSquare, review: Star, completed: CheckCircle2,
};

export function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = () => api.get("/notifications").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const h = () => load();
    window.addEventListener("bidmove:notification", h);
    return () => { clearInterval(t); window.removeEventListener("bidmove:notification", h); };
  }, []);

  const openItem = async (n) => {
    if (!n.is_read) await api.post(`/notifications/${n.id}/read`).catch(() => {});
    load();
    setOpen(false);
    if (n.job_id) navigate(`/jobs/${n.job_id}`);
    else if (n.type === "approval_request") navigate("/admin");
  };
  const readAll = async () => { await api.post("/notifications/read-all").catch(() => {}); load(); };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" className="relative outline-none p-2 rounded-lg hover:bg-secondary transition-colors">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span data-testid="notification-count" className="absolute -top-0.5 -right-0.5 h-5 min-w-[20px] px-1 rounded-full bg-kinetic text-white text-[10px] font-bold flex items-center justify-center">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 bg-card p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="font-semibold text-sm">Notifications</span>
          {data.unread > 0 && (
            <button onClick={readAll} data-testid="mark-all-read-btn" className="text-xs text-kinetic flex items-center gap-1 hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No notifications yet.</p>}
          {data.items.map((n) => {
            const Icon = ICONS[n.type] || Bell;
            return (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary/60 transition-colors flex items-start gap-3 ${n.is_read ? "" : "bg-kinetic-light/50"}`}>
                <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${n.is_read ? "bg-secondary" : "bg-kinetic-light"}`}>
                  <Icon className={`h-4 w-4 ${n.is_read ? "text-muted-foreground" : "text-kinetic"}`} />
                </div>
                <div>
                  <p className="text-sm leading-snug">{n.message}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">{new Date(n.created_at).toLocaleString()}</p>
                </div>
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
